import React from "react";
import { s } from "../styles";
import { money } from "../helpers";
import Flag from "./Flag";
import MoneyField from "./MoneyField";
import DigitCells from "./DigitCells";
import type { T4Contact, T4Employer, T4SummaryBoxes } from "../types";

interface Page1Props {
  year: number;
  employer: T4Employer;
  summary: T4SummaryBoxes;
  contact: T4Contact;
}

export default function Page1({ year, employer, summary, contact }: Page1Props) {
  const diffParts = money(summary.difference);

  return (
    <div className="t4-page">
      <div style={s.formbox}>
        <div style={s.hdr}>
          <div>
            <div style={s.crarow}>
              <Flag />
              <span style={s.craN}>Canada Revenue Agency</span>
              <span style={s.craN}>Agence du revenu du Canada</span>
            </div>
            <div style={s.yrrow}>
              <span style={s.yrtext}>
                For the year ending December 31,
                <br />
                Pour l'année se terminant le 31 décembre
              </span>
              <span style={s.yrbox}>
                20<span style={s.yr26}>{String(year).slice(-2)}</span>
              </span>
            </div>
          </div>
          <div style={s.formno}>0505</div>
          <div style={s.htitle}>
            <div>
              <span style={s.big}>T4</span>
              <span style={s.smt}>
                Summary
                <br />
                Sommaire
              </span>
            </div>
            <div style={{ fontWeight: "bold", marginTop: 3 }}>
              Summary of Remuneration Paid
            </div>
            <div style={{ fontWeight: "bold" }}>
              Sommaire de la rémunération payée
            </div>
          </div>
          <div style={s.hprot}>
            <b>Protected B</b> when completed
            <br />
            <b>Protégé B</b> une fois rempli
          </div>
        </div>

        <div style={s.body}>
          <div style={s.colL}>
            <div style={s.instr}>
              <p style={{ margin: "0 0 9px" }}>
                You have to file your T4 information return on or before the last
                day of <b>February</b>. See the information on page 2.
              </p>
              <p style={{ margin: 0 }}>
                Vous devez produire votre déclaration de renseignements T4 au
                plus tard le dernier jour de <b>février</b>. Lisez les
                renseignements à la page 2.
              </p>
            </div>
            <MoneyField
              num="88"
              centerVal
              value={summary.slips}
              label={
                <>
                  Total number of T4 slips filed
                  <br />
                  Nombre total de feuillets T4 produits
                </>
              }
            />
            <MoneyField
              num="14"
              value={summary.b14}
              label={<>Employment income &ndash; Revenus d'emploi</>}
            />
            <MoneyField
              num="20"
              value={summary.b20}
              shaded
              label={
                <>
                  Registered pension plan (RPP) contributions
                  <br />
                  Cotisations à un régime de pension agréé (RPA)
                </>
              }
            />
            <MoneyField
              num="52"
              value={summary.b52}
              shaded
              label={<>Pension adjustment &ndash; Facteur d'équivalence</>}
            />
          </div>

          <div style={s.colM}>
            <div style={s.acctbox}>
              <div style={s.acctlbl}>
                Employer's account number (15 characters) &ndash; Numéro de
                compte de l'employeur (15 caractères)
              </div>
              <div style={s.acctinner}>{employer.account}</div>
              <div style={{ ...s.acctlbl, marginTop: 6 }}>
                Name and address of employer &ndash; Nom et adresse de l'employeur
              </div>
              <div style={s.empaddr}>
                {employer.name}
                <br />
                {employer.addr1}
                <br />
                {employer.addr2}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{" "}
                {employer.prov} &nbsp;&nbsp; {employer.postal}
              </div>
            </div>
            <MoneyField
              num="16"
              value={summary.b16}
              label={
                <>
                  Employees' CPP contributions
                  <br />
                  Cotisations des employés au RPC
                </>
              }
            />
            <MoneyField
              num="16A"
              value={summary.b16A}
              label={
                <>
                  Employee's second CPP contributions
                  <br />
                  Deuxièmes cotisations de l'employé au RPC
                </>
              }
            />
            <MoneyField
              num="27"
              value={summary.b27}
              label={
                <>
                  Employer's CPP contributions
                  <br />
                  Cotisations de l'employeur au RPC
                </>
              }
            />
            <MoneyField
              num="27A"
              value={summary.b27A}
              label={
                <>
                  Employer's second CPP contributions
                  <br />
                  Deuxièmes cotisations de l'employeur au RPC
                </>
              }
            />
            <MoneyField
              num="18"
              value={summary.b18}
              label={<>Employees' EI premiums &ndash; Cotisations des employés à l'AE</>}
            />
            <MoneyField
              num="19"
              value={summary.b19}
              label={<>Employer's EI premiums &ndash; Cotisations de l'employeur à l'AE</>}
            />
            <MoneyField
              num="22"
              value={summary.b22}
              label={<>Income tax deducted &ndash; Impôt sur le revenu retenu</>}
            />
            <MoneyField
              num="80"
              value={summary.b80}
              label={
                <>
                  Total deductions reported (16+16A+27+27A+18+19+22)
                  <br />
                  Total des retenues déclarées (16+16A+27+27A+18+19+22)
                </>
              }
            />
            <MoneyField
              num="82"
              value={summary.b82}
              label={<><b>Minus:</b> remittances &ndash; <b>Moins :</b> versements</>}
            />
            <div style={s.note2}>
              <span style={{ flex: 1 }}>
                Generally, we do not charge or refund a difference of $2 or less.
              </span>
              <span style={{ flex: 1 }}>
                Généralement, une différence de 2 $ ou moins n'est ni exigée ni
                remboursée.
              </span>
            </div>
          </div>

          <div style={s.colR}>
            <div style={s.dnu}>
              <div style={s.dnuH}>
                Do not use this area
                <br />
                N'inscrivez rien ici
              </div>
              <div style={s.grp}>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <div style={{ flex: 1, textAlign: "center", fontSize: 8, lineHeight: 1.15 }}>
                    Last to current
                    <br />
                    Précédente à courante
                  </div>
                  <div style={{ width: 56, textAlign: "center", fontSize: 8, lineHeight: 1.15 }}>
                    Other
                    <br />
                    Autre
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", marginTop: 2 }}>
                  <span style={s.numbox}>90</span>
                  <span style={s.cell}>1</span>
                  <span style={s.cell}>2</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ ...s.cell, borderLeft: "1px solid #000" }}>3</span>
                </div>
              </div>
              <div style={s.grp}>
                <div style={{ ...s.cap, textAlign: "left" }}>Pro forma</div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <span style={s.numbox}>91</span>
                  <span style={s.cell}>1</span>
                  <span style={s.cell}>2</span>
                </div>
              </div>
              <div style={s.grp}>
                <div style={s.cap}>Date</div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <span style={s.numbox}>93</span>
                  <span style={{ flex: 1, border: "1px solid #000", borderLeft: "none", height: 26 }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 18, marginBottom: 10 }}>
                <div>
                  <div style={s.cap}>PD15-1</div>
                  <div style={{ display: "flex" }}>
                    <span style={s.numbox}>94</span>
                    <span style={{ ...s.cell, width: 32 }} />
                  </div>
                </div>
                <div>
                  <div style={s.cap}>POF / PSF</div>
                  <div style={{ display: "flex" }}>
                    <span style={s.numbox}>96</span>
                    <span style={{ ...s.cell, width: 32 }} />
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 18, marginBottom: 10 }}>
                <div>
                  <div style={s.cap}>NLFP / APPT</div>
                  <div style={{ display: "flex" }}>
                    <span style={s.numbox}>97</span>
                    <span style={{ ...s.cell, width: 32 }} />
                  </div>
                </div>
                <div>
                  <div style={s.cap}>NMEFP / APPEO</div>
                  <div style={{ display: "flex" }}>
                    <span style={s.numbox}>98</span>
                    <span style={{ ...s.cell, width: 32 }} />
                  </div>
                </div>
              </div>
              <div style={{ borderTop: "1px solid #000", paddingTop: 5 }}>
                Memo &ndash; Note
              </div>
              <div style={{ borderTop: "1px solid #000", marginTop: 22, paddingTop: 5 }}>
                Prepared by &ndash; Établi par
              </div>
              <div style={{ borderTop: "1px solid #000", marginTop: 22, paddingTop: 5 }}>
                Date
              </div>
            </div>
          </div>
        </div>

        <div style={s.diffrow}>
          <div style={{ width: "32%" }} />
          <div style={s.diffbox}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: 2,
              }}
            >
              <span style={{ width: 2, height: 20, background: "#000" }} />
              <span style={{ fontSize: 14, lineHeight: 0, marginTop: -4 }}>&#9660;</span>
            </div>
            <div style={s.lbl}>Difference &ndash; Différence</div>
            <div style={s.field}>
              <span style={s.val}>{diffParts.d}</span>
              <span style={s.cents}>{diffParts.c}</span>
            </div>
          </div>
        </div>

        <div style={s.branch}>
          <div style={s.bbar} />
          <div style={s.bstem} />
          <div style={s.barmL} />
          <div style={s.barmR} />
          <div style={s.barwL}>&#9660;</div>
          <div style={s.barwR}>&#9660;</div>
        </div>

        <div style={s.row3}>
          <div style={s.c84}>
            <MoneyField
              num="84"
              value={summary.b84}
              label={<>Overpayment &ndash; Paiement en trop</>}
            />
          </div>
          <div style={s.c86}>
            <MoneyField
              num="86"
              value={summary.b86}
              label={<>Balance due &ndash; Solde dû</>}
            />
          </div>
          <div style={s.cint}>
            <div style={s.lbl}>Internal use only &ndash; Pour usage interne</div>
            <div style={s.field}>
              <span style={s.val} />
            </div>
          </div>
        </div>

        <div style={s.sinrow}>
          <div style={s.sintop}>
            SIN of the proprietor(s) or principal owner(s) &ndash; NAS du ou des
            propriétaires
          </div>
          <div style={s.sinbody}>
            <div style={{ fontSize: 8.5, maxWidth: 340 }}>
              Canadian-controlled private corporations or unincorporated employers
              <br />
              Sociétés privées sous contrôle canadien ou employeurs non constitués
            </div>
            <div style={{ display: "flex", gap: 26, alignItems: "center" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={s.numbox}>74</span>
                <DigitCells n={9} />
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={s.numbox}>75</span>
                <DigitCells n={9} />
              </span>
            </div>
          </div>
        </div>

        <div style={s.contactrow}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                ...s.lbl,
                textAlign: "left",
                justifyContent: "flex-start",
              }}
            >
              Person to contact about this return
              <br />
              Personne avec qui communiquer au sujet de cette déclaration
            </div>
            <div style={s.field}>
              <span style={s.bn}>76</span>
              <span
                style={{
                  ...s.val,
                  justifyContent: "flex-start",
                  textAlign: "left",
                }}
              >
                {contact.name}
              </span>
            </div>
          </div>
          <div style={{ width: 80 }}>
            <div style={s.lbl}>
              Area code
              <br />
              Indicatif régional
            </div>
            <div style={s.field}>
              <span style={s.bn}>78</span>
              <span style={{ ...s.val, ...s.valCtr }}>{contact.areaCode}</span>
            </div>
          </div>
          <div style={{ width: 150 }}>
            <div style={s.lbl}>
              Telephone number
              <br />
              Numéro de téléphone
            </div>
            <div style={s.field}>
              <span style={{ ...s.val, ...s.valCtr }}>{contact.phone}</span>
            </div>
          </div>
          <div style={{ width: 96 }}>
            <div style={s.lbl}>
              Extension
              <br />
              Poste
            </div>
            <div style={s.field}>
              <span style={s.val}>{contact.ext}</span>
            </div>
          </div>
        </div>

        <div style={s.cert}>
          <div style={s.certT}>Certification &ndash; Attestation</div>
          <div style={{ fontSize: 9.5, lineHeight: 1.35, padding: "0 4px" }}>
            I certify that the information given on this T4 information return
            and on related slips is correct and complete.
            <br />
            J'atteste que les renseignements fournis dans cette déclaration de
            renseignements T4 et sur tous les feuillets connexes sont exacts et
            complets.
          </div>
          <div style={s.sigrow}>
            <div style={s.sig}>Date</div>
            <div style={s.sig}>
              Signature of authorized person &ndash; Signature d'une personne
              autorisée
            </div>
            <div style={s.sig}>Position or office &ndash; Titre ou poste</div>
          </div>
        </div>
      </div>

      <div style={s.foot}>
        <div>
          See the privacy notice at the bottom of next page.
          <br />
          Consultez l'avis de confidentialité au bas de la page suivante.
          <br />
          <b style={{ fontSize: 11 }}>T4 SUM (25)</b>
        </div>
        <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12 }}>
          REV &nbsp;&nbsp; OSP
        </div>
        <div style={s.cn}>Canada</div>
      </div>
    </div>
  );
}